import { ReactNode } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { N200, N700 } from "@atlaskit/theme/colors";
import { token } from "@atlaskit/tokens";

import { Adg4Button } from "@/components/adg4/button/src";
import {
    fontSize,
    getProductData,
    ProductData,
    ProductKey,
    getSwitchAccountUrl
} from "@/components/signup/utils";
import { useProductD0to6Site, useUserContext } from "@/components/user-context";
import { wacToken } from "@/libraries/design/foundations";

import { useSignupFormContext } from "../../../signup-form/hooks";
import { WelcomeBackHeading } from "../welcome-back-heading";

export const CobrandedReturnToExistingProduct = ({
    exitView,
    productKeyForExpand
}: {
    exitView: () => void;
    productKeyForExpand: ProductKey;
}) => {
    const d0to6JiraSite = useProductD0to6Site({
        productKey: productKeyForExpand
    });
    const { productKey } = useSignupFormContext();
    const {
        user: { email, name }
    } = useUserContext();
    const { productName } = getProductData(
        productKey as keyof typeof ProductData
    );
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const intl = useIntl();

    const goToProductButtonText =
        productKeyForExpand === ProductKey.CONFLUENCE
            ? intl.formatMessage({
                  id: "bxpSignup.siteExpansion.goToWorkspace",
                  defaultMessage: "Go to workspace"
              })
            : intl.formatMessage(
                  {
                      id: "bxpSignup.siteExpansion.goToProduct",
                      defaultMessage: "Go to {productName}"
                  },
                  {
                      productName
                  }
              );

    const createNewWorkspaceMessageValues = {
        link: (str: ReactNode) => (
            <Adg4Button css={createNewWorkspaceLinkCss} onClick={exitView}>
                {str}
            </Adg4Button>
        )
    };

    const createNewWorkspaceMessage =
        productKeyForExpand === ProductKey.CONFLUENCE
            ? intl.formatMessage(
                  {
                      id: "BxpSignup.SiteExpansionForm.cobrandedConfluenceCreateNewWorkspaceLink",
                      defaultMessage: "or <link>create a new workspace</link>"
                  },
                  createNewWorkspaceMessageValues
              )
            : intl.formatMessage(
                  {
                      id: "BxpSignup.SiteExpansionForm.cobrandedJiraCreateNewWorkspaceLink",
                      defaultMessage: "or <link>start a new site</link>"
                  },
                  createNewWorkspaceMessageValues
              );

    return !d0to6JiraSite ? null : (
        <div css={containerCss}>
            <div css={formContainerCss}>
                <div css={headerGroupCss}>
                    <h1 css={headerCss}>
                        <WelcomeBackHeading name={name} />
                    </h1>
                    <p css={headerCss}>
                        <FormattedMessage
                            id="BxpSignup.SiteExpansionForm.cobrandedSubheader.1"
                            defaultMessage="Pick up where you left off in {productName}"
                            values={{ productName }}
                        />
                    </p>
                </div>
                <p css={switchAccountSubheader}>
                    <FormattedMessage
                        id="BxpSignup.SiteExpansionForm.cobrandedSubheader.2"
                        defaultMessage="You’re logged in as {email}. <switchAccountLink>Switch account</switchAccountLink>"
                        values={{
                            email: <strong css={emailCss}>{email}</strong>,
                            switchAccountLink
                        }}
                    />
                </p>
                <div
                    css={boxContainerCss}
                    data-testid="return-to-existing-product"
                >
                    <a
                        css={siteBoxCss}
                        href={getProductUrl({
                            url: d0to6JiraSite.url,
                            productKey
                        })}
                        onClick={(e) => {
                            e.preventDefault(); // wait for event to fire
                            createAnalyticsEvent({
                                eventType: "ui",
                                action: "clicked",
                                actionSubject: "button",
                                actionSubjectId:
                                    "contactExpandClickReturnToProductButton",
                                attributes: {
                                    productName
                                }
                            }).fire();
                            window.location.href = getProductUrl({
                                url: d0to6JiraSite.url,
                                productKey
                            });
                        }}
                    >
                        <div css={siteCss}>
                            <p css={bold}>{productName}</p>
                            <div css={siteNameCss}>
                                {d0to6JiraSite.displayName}
                            </div>
                        </div>
                        <Adg4Button variant="primary" size="small" color="blue">
                            {goToProductButtonText}
                        </Adg4Button>
                    </a>
                </div>
            </div>
            <div css={createNewWorkspaceMessageCss}>
                {createNewWorkspaceMessage}
            </div>
        </div>
    );
};

const switchAccountLink = (children: ReactNode) => (
    <a href={getSwitchAccountUrl()} css={switchAccountLinkCss}>
        {children}
    </a>
);
const getProductUrl = ({
    url,
    productKey
}: {
    url: string;
    productKey: string;
}) => {
    switch (productKey) {
        case ProductKey.JIRA_SOFTWARE:
            return `${url}/jira`;
        case ProductKey.CONFLUENCE:
            return `${url}/wiki`;
        default:
            return url;
    }
};

const containerCss = css({
    display: "grid",
    minHeight: "520px",
    alignContent: "space-between"
});

const formContainerCss = css({
    display: "grid",
    alignItems: "center",
    color: N700,
    gap: "24px",
    margin: "auto 0"
});

const headerGroupCss = css({
    display: "grid",
    gap: token("space.100")
});

const headerCss = css({
    margin: 0,
    textAlign: "center"
});

const switchAccountSubheader = css({
    margin: 0,
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "14px",
    textAlign: "center"
});

const createNewWorkspaceMessageCss = css({
    fontSize: 14,
    fontWeight: 500,
    textAlign: "center"
});

const createNewWorkspaceLinkCss = css({
    backgroundColor: "transparent !important",
    border: 0,
    borderRadius: 0,
    color: wacToken("color.primary.blue"),
    cursor: "pointer",
    fontSize: "inherit",
    fontWeight: "inherit",
    minHeight: "auto",
    minWidth: "auto",
    padding: "0 !important",
    textDecoration: "underline !important",
    [":hover"]: {
        color: wacToken("color.extended.blue800")
    }
});

const boxContainerCss = css({
    alignItems: "center",
    display: "grid",
    width: "100%",
    border: `1px solid ${wacToken("color.neutral.light400")}`,
    padding: "16px",
    borderRadius: "8px"
});

const siteBoxCss = css({
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    [":hover"]: {
        textDecoration: "none !important"
    }
});

const bold = css({
    margin: 0,
    fontWeight: "400"
});

const siteCss = css({
    fontSize: fontSize.md,
    overflow: "hidden"
});

const siteNameCss = css({
    color: N200,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "14px"
});

const emailCss = css({
    fontWeight: 600,
    hyphens: "auto",
    overflowWrap: "break-word",
    wordBreak: "break-word",
    wordWrap: "break-word"
});

const switchAccountLinkCss = css({
    textDecoration: "underline",
    color: wacToken("color.primary.blue"),
    whiteSpace: "nowrap",
    [":hover"]: {
        color: wacToken("color.extended.blue800")
    }
});
